/**
 * API Client Utilities
 * Centralized HTTP communication with the backend
 */

// Use empty base for development (proxied through Vite)
// In production, set VITE_API_URL environment variable
const API_BASE = import.meta.env.VITE_API_URL || '';

// Optional mock user mode for local development
const USE_MOCK_USER =
  import.meta.env.DEV && String(import.meta.env.VITE_USE_MOCK_USER || '').toLowerCase() === 'true';

/**
 * Construct full API URL from path
 */
export function getApiUrl(path) {
  // If API_BASE is empty, just return the path (for Vite proxy)
  if (!API_BASE) {
    return path.startsWith('/') ? path : '/' + path;
  }
  return `${API_BASE}${path.startsWith('/') ? path : '/' + path}`;
}

/**
 * Default fetch options
 * Include credentials for cookies; optional mock user header for development
 */
export const fetchOpts = {
  credentials: 'include',
  headers: {
    'Content-Type': 'application/json',
    ...(USE_MOCK_USER ? { 'X-Mock-User-Id': 'dev-user-123' } : {})
  }
};

/**
 * Fetch and parse JSON response
 * Throws error if response is not ok
 */
export async function fetchJson(path, options = {}) {
  const url = getApiUrl(path);
  const response = await fetch(url, { ...fetchOpts, ...options });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`API Error ${response.status}: ${text}`);
  }

  return response.json();
}

/**
 * POST request with JSON body
 */
export async function postJson(path, body) {
  return fetchJson(path, {
    method: 'POST',
    body: JSON.stringify(body)
  });
}

/**
 * PUT request with JSON body
 */
export async function putJson(path, body) {
  return fetchJson(path, {
    method: 'PUT',
    body: JSON.stringify(body)
  });
}

/**
 * DELETE request
 */
export async function deleteJson(path) {
  return fetchJson(path, {
    method: 'DELETE'
  });
}
